import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { RELEASES } from "@/data/catalog";
import { usePlayer } from "@/context/PlayerContext";
import { Play } from "lucide-react";

const FILTERS = ["All", "Bambazi", "H2o", "OEo", "Tsunami", "Naima Imani", "24kt", "Fuego"];

const ReleaseCard = ({ r, index, big }) => {
  const { play } = usePlayer();
  return (
    <motion.button
      layout
      data-testid={`release-card-${r.ytId}`}
      onClick={() => play(r)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="group relative text-left border border-white/10 overflow-hidden bg-charcoal hover:border-signal/60 transition-colors"
    >
      <div className={`relative overflow-hidden ${big ? "aspect-[4/3]" : "aspect-square"}`}>
        <img
          src={r.cover}
          alt={r.title}
          loading="lazy"
          className="h-full w-full object-cover grayscale group-hover:grayscale-0 scale-[1.02] group-hover:scale-110 transition-all duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-void via-void/20 to-transparent" />

        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="font-mono text-[10px] tracking-[0.2em] text-white/70 bg-void/60 backdrop-blur px-2 py-1">{r.cat}</span>
          {r.featured && <span className="font-mono text-[10px] tracking-[0.2em] text-void bg-signal px-2 py-1">FEATURED</span>}
        </div>

        <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="grid place-items-center h-14 w-14 rounded-full bg-signal text-void translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
            <Play size={20} className="fill-current ml-1" />
          </span>
        </div>

        <div className="absolute bottom-0 inset-x-0 p-4">
          <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-signal mb-1">
            {r.artist}{r.feat ? ` · ${r.feat}` : ""}
          </p>
          <h3 className="font-display uppercase leading-none tracking-tight text-white text-2xl md:text-3xl">{r.title}</h3>
        </div>
      </div>
      <div className="flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-[0.2em] text-white/50">
        <span>{r.duration}</span>
        <span>{r.plays} PLAYS</span>
      </div>
    </motion.button>
  );
};

export const Catalog = () => {
  const [filter, setFilter] = useState("All");
  const list = useMemo(
    () => (filter === "All" ? RELEASES : RELEASES.filter((r) => r.artist === filter)),
    [filter]
  );

  return (
    <section id="releases" data-testid="catalog" className="relative bg-void py-24 md:py-32 px-5 md:px-10">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14 md:mb-20">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs text-signal mb-4">[ The Catalog ]</p>
            <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-white text-6xl md:text-8xl">
              Every <span className="text-stroke">Record</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`font-mono uppercase tracking-[0.15em] text-[11px] px-4 py-2 border transition-colors ${
                  filter === f
                    ? "bg-signal text-void border-signal"
                    : "border-white/15 text-white/55 hover:text-white hover:border-white/40"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {list.map((r, i) => (
            <ReleaseCard key={r.ytId} r={r} index={i} big={filter === "All" && i < 2} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
